import React, { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { Well } from '@pushly/aqe/lib/components'
import { useRefState, useService } from '@pushly/aqe/lib/hooks'
import { simpleNotification } from '@pushly/aqe/lib/utils'
import {
    JourneyNode,
    NodeEditorRef,
    NodeEditorSubmitResult,
    SendActionStep,
    isActionStep,
} from '../../types/journey-nodes'
import { updateStepConfig } from '@pushly/models/lib/structs/journeys'
import type { IJourneyActionNotificationRequest } from '@pushly/models/lib/structs/journeys'
import { JourneyActionType } from '@pushly/models/lib/enums/journeys/journey-action-type'
import { Alert, Button, Cascader, Divider, Flex, Skeleton, Space, Tabs } from 'antd'
import {
    BlueConicRecommendedNotificationIntegration,
    BlueConicRecommendedNotificationParams,
    isBlueConicRecommendedNotificationParams,
} from '@pushly/models/lib/structs/journeys'
import { RecommendationSource } from '@pushly/models/lib/enums/recommendations/recommendation-source'
import { GetBlueConicIntegrationResponse, GetDomainIntegrationResponse } from '@pushly/models/lib/structs/domains'
import { DomainIntegrationProviders } from '@pushly/models/lib/enums/domain-integration-providers'
import { PropertiesOf } from '@pushly/models/lib/types'
import { getWrappedMacro, Macro, PlatformIntegrationOwnerType } from '@pushly/models/lib/enums'
import { useJourneyContext } from '../../context'
import { BlueConicRecommendationEditor } from './blueconic/blueconic-recommendation-editor'
import { BLUECONIC_DISPLAY_VALUE, MAX_BLUECONIC_RECOMMENDATION_CONIGS_ALLOWED } from './blueconic/constants'
import { Loading3QuartersOutlined } from '@ant-design/icons'
import { DomainIntegrationService } from '../../../../services'
import { validateRecommendedNotification } from './helpers'
import { alphaSort } from '../../../../_utils/array'
import classnames from 'classnames'
import { JourneyBuilderMode } from '../../enums'
import { SendNotificationEditor } from '../send-notification-editor'
import { JourneyAction } from '../../types/journey-context'
import { NotificationVariantModel } from '../../../../models/notification/notification-variant.model'
import { VERTICAL_WELL_GAP } from '@pushly/aqe'

import './send-recommended-notification-editor.scss'

type SendRecommendedNotificationEditorProps = {
    node: JourneyNode<SendActionStep<JourneyActionType.SEND_RECOMMENDED_NOTIFICATION>>
    ref: ForwardedRef<NodeEditorRef>
}

type Option = {
    value: string | number
    label: React.ReactNode
    children?: Option[]
}

type ActiveTabKey = 'configuration' | 'notification'

export const SendRecommendedNotificationEditor = forwardRef<NodeEditorRef, SendRecommendedNotificationEditorProps>(
    ({ node }, ref) => {
        const domainIntegrationSvc = useService(DomainIntegrationService)
        const [state, dispatch] = useJourneyContext()

        const [loadingIntegrations, setLoadingIntegrations] = useState(false)
        const [sourceOptions, setSourceOptions] = useState<Option[]>([])
        const [activeTab, setActiveTab] = useState<ActiveTabKey>('configuration')

        const notifEditorRef = useRef<NodeEditorRef>(null)
        const handleSubmitRef = useRef<(() => Promise<NodeEditorSubmitResult>) | null>(null)

        const [localValueRef, setLocalValue] = useRefState<BlueConicRecommendedNotificationParams>(
            isBlueConicRecommendedNotificationParams(node.data.step.configuration.params)
                ? node.data.step.configuration.params.clone()
                : BlueConicRecommendedNotificationParams.parseJsonObject(node.data.step.configuration.params),
        )
        const localValue = localValueRef.current

        const onChange = (change: PropertiesOf<BlueConicRecommendedNotificationParams>) => {
            setLocalValue(localValue.clone(change))
        }

        handleSubmitRef.current = async (): Promise<NodeEditorSubmitResult> => {
            const configurationUpdates = await handleConfigurationSubmit(localValue)

            if (configurationUpdates) {
                // Clear invalid state when the configuration has been re-validated successfully
                const clearInvalid = !!node.data.uiMeta.invalid

                const existingNotification = node.data.step.configuration.params.notification
                const innerResult = await notifEditorRef.current?.onSubmit()

                // Determine whether the user actually edited the notification.
                // The inner SendNotificationEditor returns undefined when untouched.
                const notificationEdited = !!innerResult
                let notificationResult: typeof existingNotification
                if (innerResult && isActionStep(innerResult.step)) {
                    notificationResult = innerResult.step.configuration.params.notification
                } else {
                    // Notification wasn't edited — fall back to existing data
                    notificationResult = existingNotification
                }

                if (!notificationResult) {
                    return
                }

                // Build the new params combining recommendation config + notification.
                // Only clear notification_id if the notification was actually edited —
                // config-only changes should preserve the existing reference.
                const newParams = {
                    ...configurationUpdates,
                    notification: notificationResult,
                    notification_id: notificationEdited ? undefined : configurationUpdates.notification_id,
                }

                // If nothing actually changed compared to the original step, treat as
                // a no-op — avoids unnecessary dispatches and downstream API calls.
                // Compare notification via JSON, and config via serialize() which
                // produces stable plain-object representations for both sides.
                const originalParams = node.data.step.configuration.params
                const notificationUnchanged =
                    JSON.stringify(notificationResult) === JSON.stringify(originalParams.notification)
                const originalForCompare = isBlueConicRecommendedNotificationParams(originalParams)
                    ? originalParams
                    : BlueConicRecommendedNotificationParams.parseJsonObject(originalParams)
                const configUnchanged = configurationUpdates.equals(originalForCompare, {
                    omittingKeys: ['notification', 'notification_id'],
                })

                if (notificationUnchanged && configUnchanged) {
                    return
                }

                // Build a new step object immutably combining recommendation config + notification.
                // When notification was edited, clear notification_id so the inline payload
                // survives journey-wizard.tsx's dedup logic (which strips `notification`
                // when both `notification_id` and `notification` are present).
                const newStep = updateStepConfig(node.data.step, { params: newParams })

                return {
                    nodeId: node.id,
                    step: newStep,
                    clearInvalid,
                }
            }
        }

        useImperativeHandle(ref, () => ({ onSubmit: handleSubmitRef.current! }))

        useEffect(() => {
            const getRecommendationIntegrations = async () => {
                setLoadingIntegrations(true)
                let integrations: Map<number, GetDomainIntegrationResponse> =
                    state.dependencies?.integrations ?? new Map()
                if (!integrations.size) {
                    const svcRes = await domainIntegrationSvc.fetchAll(state.domain.id)

                    if (svcRes.ok && svcRes.data?.length) {
                        integrations = new Map(
                            svcRes.data.map<[number, GetDomainIntegrationResponse]>((resInt) => [
                                resInt.id,
                                GetDomainIntegrationResponse.parseJsonObject(resInt),
                            ]),
                        )
                    }
                }

                const blueConicSourceOptions = Array.from(integrations.values())
                    .filter((datum) => datum.provider === DomainIntegrationProviders.BLUECONIC_REC)
                    .map((datum) => ({
                        label: (datum.data as GetBlueConicIntegrationResponse).name,
                        value: datum.id,
                        children: Object.keys((datum.data as GetBlueConicIntegrationResponse).stores)
                            .map((store_id) => ({
                                label: ((datum.data as GetBlueConicIntegrationResponse).stores[store_id] as any).name,
                                value: store_id,
                            }))
                            .sort(({ label: aLabel }, { label: bLabel }) => alphaSort(aLabel, bLabel)),
                    }))
                    .sort(({ label: aLabel }, { label: bLabel }) => alphaSort(aLabel, bLabel))

                setSourceOptions([
                    {
                        label: (
                            <span className="integration-label">
                                <span className="integration-label-source">{BLUECONIC_DISPLAY_VALUE}</span>
                            </span>
                        ),
                        value: RecommendationSource.BLUECONIC,
                        children: blueConicSourceOptions,
                    },
                ])

                setLoadingIntegrations(false)
            }

            getRecommendationIntegrations()
        }, [])

        useEffect(() => {
            dispatch({
                action: JourneyAction.SET_EDITOR_FOOTER,
                data: (
                    <Space>
                        {activeTab === 'notification' && (
                            <Button
                                type="default"
                                onClick={() =>
                                    handleTabChange(
                                        'configuration',
                                        activeTab,
                                        localValueRef,
                                        notifEditorRef,
                                        setActiveTab,
                                        node.data.step.configuration.params.notification,
                                    )
                                }
                            >
                                Back: Configuration
                            </Button>
                        )}
                        <Button
                            type="primary"
                            onClick={async () => {
                                if (activeTab === 'configuration') {
                                    await handleTabChange(
                                        'notification',
                                        activeTab,
                                        localValueRef,
                                        notifEditorRef,
                                        setActiveTab,
                                        node.data.step.configuration.params.notification,
                                    )
                                    return
                                }

                                const nodeChange = await handleSubmitRef.current!()
                                if (nodeChange) {
                                    dispatch({ action: JourneyAction.ON_NODE_STEP_CHANGE, data: nodeChange })
                                    dispatch({ action: JourneyAction.SET_ACTIVE_NODE, data: null })
                                }
                            }}
                        >
                            {activeTab === 'notification' ? 'Update Step' : 'Next: Notification'}
                        </Button>
                    </Space>
                ),
            })
        }, [activeTab])

        return (
            <Flex
                vertical
                gap={VERTICAL_WELL_GAP}
                id="send-recommended-notification-editor"
                className="send-recommended-notification-editor"
            >
                {state.mode === JourneyBuilderMode.READONLY && (
                    <>
                        <Alert type="info" message="You are currently viewing this step in View-Only Mode." showIcon />
                        <Divider type="horizontal" />
                    </>
                )}
                <Well
                    className="recommendation-source-section"
                    variant="ghost"
                    footerMode="hidden"
                    padding="none"
                    title="Source"
                >
                    <Cascader<Option>
                        className={classnames(state.mode === JourneyBuilderMode.READONLY ? 'view-only' : undefined)}
                        placeholder={
                            loadingIntegrations ? (
                                <Loading3QuartersOutlined spin={true} />
                            ) : (
                                'Select a Recommendation Integration Source'
                            )
                        }
                        disabled={loadingIntegrations}
                        classNames={{ popup: { root: 'integration-cascader-popup' } }}
                        value={
                            localValue?.recommendation_source
                                ? [
                                      localValue?.recommendation_source,
                                      localValue?.integration?.integration_id,
                                      localValue?.integration?.store_id,
                                  ]
                                : undefined
                        }
                        allowClear={false}
                        options={sourceOptions}
                        expandTrigger="hover"
                        onChange={([recommendation_source, integration_id, store_id]) => {
                            onChange({
                                recommendation_config: localValue.recommendation_config,
                                recommendation_source: recommendation_source as RecommendationSource.BLUECONIC,
                                integration: BlueConicRecommendedNotificationIntegration.from({
                                    integration_owner_type: PlatformIntegrationOwnerType.DOMAIN,
                                    integration_id: parseInt(String(integration_id), 10),
                                    store_id: String(store_id),
                                }),
                                notification: localValue.notification,
                                notification_id: localValue.notification_id,
                            })
                        }}
                        style={{ width: 400 }}
                    />
                </Well>

                <Tabs
                    activeKey={activeTab}
                    onChange={(next: ActiveTabKey) =>
                        handleTabChange(
                            next,
                            activeTab,
                            localValueRef,
                            notifEditorRef,
                            setActiveTab,
                            node.data.step.configuration.params.notification,
                        )
                    }
                    items={[
                        {
                            key: 'configuration',
                            label: (
                                <div>
                                    Configurations &nbsp;
                                    {localValue?.recommendation_source === RecommendationSource.BLUECONIC && (
                                        <span className="size-label">
                                            ({localValue.recommendation_config?.length}/
                                            {MAX_BLUECONIC_RECOMMENDATION_CONIGS_ALLOWED})
                                        </span>
                                    )}
                                </div>
                            ),
                            children: (
                                <Well
                                    className="recommendation-configurations-builder-section"
                                    mode="ghost"
                                    padding="none"
                                    footerMode="hidden"
                                    title="Recommendation Configurations"
                                >
                                    {loadingIntegrations ? (
                                        <Skeleton paragraph={{ rows: 10 }} active loading={loadingIntegrations} />
                                    ) : !localValue ||
                                      !localValue?.recommendation_source ||
                                      !localValue?.integration.integration_id ||
                                      !localValue?.integration.store_id ? (
                                        <span>
                                            Please select a Recommendation Integration Source to continue configuring
                                            recommendations
                                        </span>
                                    ) : localValue?.recommendation_source === RecommendationSource.BLUECONIC ? (
                                        <BlueConicRecommendationEditor
                                            readonly={state.mode === JourneyBuilderMode.READONLY}
                                            domain={state.domain}
                                            integration_id={123456}
                                            blueConicRecommendation={localValue}
                                            onChange={onChange}
                                            ref={ref}
                                        />
                                    ) : null}
                                </Well>
                            ),
                        },
                        {
                            key: 'notification',
                            label: 'Notification Setup',
                            children: (
                                <SendNotificationEditor
                                    node={node}
                                    ref={notifEditorRef}
                                    builderProps={{
                                        macroProps: {
                                            categories: {
                                                urlFields: [
                                                    'domain',
                                                    'device',
                                                    'location',
                                                    'article_recommendations',
                                                    'notification',
                                                    'secrets',
                                                    'custom',
                                                ],
                                                messageFields: [
                                                    'location',
                                                    'article_recommendations',
                                                    'secrets',
                                                    'custom',
                                                ],
                                            },
                                            imageMacroProps: {
                                                tag: Macro.ARTICLE_IMAGE,
                                                label: 'Use article image',
                                            },
                                        },
                                        onInitialVariantInit: (variant: NotificationVariantModel) => {
                                            const content = variant.getContent().getDefaultContent()

                                            let landingUrl = getWrappedMacro(Macro.ARTICLE_URL)
                                            const title = getWrappedMacro(Macro.ARTICLE_TITLE)
                                            const body = getWrappedMacro(Macro.ARTICLE_DESCRIPTION, {
                                                defaultValue: '',
                                            })
                                            const imageUrl = getWrappedMacro(Macro.ARTICLE_IMAGE)

                                            switch (localValue.recommendation_source) {
                                                case RecommendationSource.BLUECONIC: {
                                                    landingUrl = getWrappedMacro(Macro.ARTICLE_TRACKING_URL)
                                                    break
                                                }
                                            }

                                            content.setLandingUrl(landingUrl)
                                            content.setTitle(title)
                                            content.setBody(body)
                                            content.setImageUrl(imageUrl)
                                        },
                                    }}
                                />
                            ),
                        },
                    ]}
                />
            </Flex>
        )
    },
)

async function handleNotificationSubmit(
    nodeEditor: NodeEditorRef | null,
    existingNotification: IJourneyActionNotificationRequest | undefined,
) {
    const result = await nodeEditor?.onSubmit()
    if (!result) {
        // The inner SendNotificationEditor returns undefined when the user
        // hasn't edited the notification (preserving notification_id).
        // In the recommended editor context, config-only changes still need
        // the notification payload to compose the full step update — fall
        // back to the existing notification data from the node.
        return existingNotification
    }
    if (!isActionStep(result.step)) {
        return undefined
    }
    return result.step.configuration.params.notification
}

async function handleConfigurationSubmit(config: BlueConicRecommendedNotificationParams) {
    const [isValid, errorMsg] = validateRecommendedNotification(config)
    if (!isValid) {
        simpleNotification('error', errorMsg)
        return null
    }

    return config
}

async function handleTabChange(
    nextTabKey: ActiveTabKey,
    currTabKey: ActiveTabKey,
    localValueRef: React.RefObject<BlueConicRecommendedNotificationParams>,
    notifEditorRef: React.RefObject<NodeEditorRef>,
    setter: (value: React.SetStateAction<ActiveTabKey>) => void,
    existingNotification?: IJourneyActionNotificationRequest,
) {
    if (nextTabKey === currTabKey) return

    if (nextTabKey === 'configuration') {
        const notifUpdates = await handleNotificationSubmit(notifEditorRef.current, existingNotification)
        if (notifUpdates) {
            setter(nextTabKey)
        }
    } else {
        const configUpdates = await handleConfigurationSubmit(localValueRef.current!)
        if (configUpdates) {
            setter(nextTabKey)
        }
    }
}
